import { createClient } from "@supabase/supabase-js";
import {
  requestDeploymentVersionCheck,
  shouldRequestDeploymentVersionCheck,
} from "./deploymentUpdateRuntime.js";
import * as runtimeRequestProfiling from "./runtimeRequestProfiling.js";
import { PREFERENCES_FOR_HEADER, preferencesTargetHeaderFor, isUserUiPreferencesUrl } from "../utils/preferencesDelegation.js";
import { beginUserUiPreferencesWrite } from "./userUiPreferencesSync.js";
import {
  clearSupabaseAuthStorageNamespace,
  createTabScopedAuthStorage,
  LOCAL_AUTH_BYPASS_SESSION_STATE_STORAGE_KEY,
} from "./tabScopedAuthStorage.js";

const LOCAL_AUTH_BYPASS_TOKEN = "grain-local-auth-bypass-token";
const LOCAL_AUTH_BYPASS_USER_ID = "grain-local-auth-bypass-user";
const LOCAL_AUTH_BYPASS_SESSION_STATE_CHANGE_EVENT = "grain:local-auth-bypass-session-state-change";
const LOCAL_AUTH_BYPASS_ACTIVE_STATE = "active";
const LOCAL_AUTH_BYPASS_SIGNED_OUT_STATE = "signed-out";
export const SUPABASE_AUTH_REJECTED_EVENT = "grain:supabase-auth-rejected";
let localAuthBypassSessionState = "";
let rejectedSessionInvalidationPromise = null;

function isTruthyEnvFlag(value) {
  if (typeof value !== "string") {
    return false;
  }

  return ["1", "true", "yes", "on"].includes(value.trim().toLowerCase());
}

function isLoopbackHostname(hostname) {
  return hostname === "127.0.0.1"
    || hostname === "localhost"
    || hostname === "::1"
    || hostname === "[::1]";
}

const importMetaEnv = typeof import.meta === "object"
  && import.meta
  && typeof import.meta.env === "object"
  && import.meta.env
  ? import.meta.env
  : {};

const supabaseUrl = typeof importMetaEnv.VITE_SUPABASE_URL === "string"
  ? importMetaEnv.VITE_SUPABASE_URL.trim()
  : "";
const supabaseAnonKey = typeof importMetaEnv.VITE_SUPABASE_ANON_KEY === "string"
  ? importMetaEnv.VITE_SUPABASE_ANON_KEY.trim()
  : "";

const supabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);
const supabaseWindow = typeof window !== "undefined"
  ? window
  : undefined;
// This runs before createClient so a /login?switchAccount=1 navigation cannot
// restore a session cloned from the tab that opened it.
const tabScopedAuthStorage = createTabScopedAuthStorage({ targetWindow: supabaseWindow });
const localAuthBypassEnabled = isTruthyEnvFlag(importMetaEnv.VITE_LOCAL_AUTH_BYPASS)
  && isLoopbackHostname(supabaseWindow?.location?.hostname || "");

export function getTabScopedSupabaseStorageKey() {
  return tabScopedAuthStorage.storageKey;
}

function getLocalAuthBypassStorage() {
  return tabScopedAuthStorage.storage;
}

function readLocalAuthBypassSessionState() {
  const storage = getLocalAuthBypassStorage();
  if (storage) {
    try {
      const rawState = storage.getItem(LOCAL_AUTH_BYPASS_SESSION_STATE_STORAGE_KEY);
      return typeof rawState === "string" ? rawState.trim().toLowerCase() : "";
    } catch {}
  }

  return localAuthBypassSessionState;
}

function getLocalAuthBypassSession() {
  if (!localAuthBypassEnabled) {
    return null;
  }

  return readLocalAuthBypassSessionState() === LOCAL_AUTH_BYPASS_ACTIVE_STATE
    ? buildLocalAuthBypassSession()
    : null;
}

function dispatchLocalAuthBypassSessionStateChange(targetWindow = supabaseWindow) {
  if (!targetWindow?.dispatchEvent) {
    return;
  }

  try {
    targetWindow.dispatchEvent(new Event(LOCAL_AUTH_BYPASS_SESSION_STATE_CHANGE_EVENT));
  } catch {}
}

function setLocalAuthBypassSessionActive(isActive, targetWindow = supabaseWindow) {
  const nextState = isActive
    ? LOCAL_AUTH_BYPASS_ACTIVE_STATE
    : LOCAL_AUTH_BYPASS_SIGNED_OUT_STATE;
  localAuthBypassSessionState = nextState;
  const storage = getLocalAuthBypassStorage();

  if (storage) {
    try {
      storage.setItem(LOCAL_AUTH_BYPASS_SESSION_STATE_STORAGE_KEY, nextState);
    } catch {}
  }

  dispatchLocalAuthBypassSessionStateChange(targetWindow);
}

function buildLocalAuthBypassSession() {
  return {
    access_token: LOCAL_AUTH_BYPASS_TOKEN,
    expires_at: Math.floor(Date.now() / 1000) + 60 * 60 * 24,
    expires_in: 60 * 60 * 24,
    refresh_token: "grain-local-auth-bypass-refresh-token",
    token_type: "bearer",
    user: {
      app_metadata: {
        provider: "local-auth-bypass",
        role: "admin",
        roles: ["admin"],
      },
      aud: "authenticated",
      email: "local-bypass@grain.local",
      id: LOCAL_AUTH_BYPASS_USER_ID,
      role: "authenticated",
      user_metadata: {
        auth_bypass: true,
      },
    },
  };
}

const cachedSupabaseClient = supabaseWindow?.__grainSupabaseClient__;
const cachedSupabaseClientStorageKey = supabaseWindow?.__grainSupabaseClientStorageKey__;
const canReuseCachedSupabaseClient = cachedSupabaseClient
  && cachedSupabaseClientStorageKey === tabScopedAuthStorage.storageKey;

export const supabase = supabaseConfigured
  ? canReuseCachedSupabaseClient ? cachedSupabaseClient : createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      detectSessionInUrl: true,
      persistSession: tabScopedAuthStorage.persistSession,
      storage: tabScopedAuthStorage.storage,
      storageKey: tabScopedAuthStorage.storageKey,
    },
  })
  : null;

if (supabaseWindow && supabase) {
  supabaseWindow.__grainSupabaseClient__ = supabase;
  supabaseWindow.__grainSupabaseClientStorageKey__ = tabScopedAuthStorage.storageKey;
}

export function isSupabaseConfigured() {
  return supabaseConfigured || localAuthBypassEnabled;
}

export function isLocalAuthBypassEnabled() {
  return localAuthBypassEnabled;
}

export async function activateLocalAuthBypassSession() {
  if (!localAuthBypassEnabled) {
    return false;
  }

  if (supabase) {
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {}
  }

  setLocalAuthBypassSessionActive(true);
  return true;
}

async function getPersistedSupabaseSession() {
  if (!supabase) {
    return { error: null, session: null };
  }

  const { data, error } = await supabase.auth.getSession();
  return {
    error,
    session: data?.session ?? null,
  };
}

export async function getSupabaseSession() {
  const persistedSessionResult = await getPersistedSupabaseSession();
  if (persistedSessionResult.error || persistedSessionResult.session) {
    return persistedSessionResult;
  }

  if (localAuthBypassEnabled) {
    return { error: null, session: getLocalAuthBypassSession() };
  }

  return persistedSessionResult;
}


export async function getSupabaseAccessToken() {
  const { error, session } = await getSupabaseSession();
  if (error) {
    throw error;
  }

  const accessToken = typeof session?.access_token === "string"
    ? session.access_token.trim()
    : "";
  return accessToken || null;
}


export async function signOutSupabaseSession(options = { scope: "local" }) {
  if (localAuthBypassEnabled) {
    if (supabase) {
      try {
        await supabase.auth.signOut(options);
      } catch {}
    }

    setLocalAuthBypassSessionActive(false);
    return { error: null };
  }

  if (!supabase) {
    return { error: null };
  }

  return supabase.auth.signOut(options);
}


function dispatchSupabaseAuthRejected(targetWindow = supabaseWindow) {
  if (!targetWindow?.dispatchEvent) {
    return;
  }

  try {
    targetWindow.dispatchEvent(new Event(SUPABASE_AUTH_REJECTED_EVENT));
  } catch {}
}


function invalidateRejectedSupabaseSession() {
  if (rejectedSessionInvalidationPromise) {
    return rejectedSessionInvalidationPromise;
  }

  dispatchSupabaseAuthRejected();
  rejectedSessionInvalidationPromise = Promise.resolve()
    .then(() => signOutSupabaseSession({ scope: "local" }))
    .catch(() => null)
    .finally(() => {
      clearSupabaseAuthStorageNamespace(tabScopedAuthStorage.storage, tabScopedAuthStorage.tabId);
      rejectedSessionInvalidationPromise = null;
    });
  return rejectedSessionInvalidationPromise;
}


export async function fetchWithSupabaseAuth(input, init = {}) {
	const hasAuthAccessTokenOverride = Object.prototype.hasOwnProperty.call(init || {}, "authAccessToken");
	const { authAccessToken, fetchImpl, ...requestInit } = init || {};
  const resolvedFetch = typeof fetchImpl === "function"
    ? fetchImpl
    : (typeof globalThis.fetch === "function" ? globalThis.fetch.bind(globalThis) : null);
  if (!resolvedFetch) {
    throw new Error("Requête API indisponible: fetch n'est pas supporté.");
  }

  const headers = new Headers(requestInit.headers || {});
	const accessToken = hasAuthAccessTokenOverride
		? (typeof authAccessToken === "string" ? authAccessToken : "")
		: await getSupabaseAccessToken();
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  const requestUrl = typeof input === "string"
    ? input
    : (typeof input?.url === "string" ? input.url : String(input));
  const preferencesFor = preferencesTargetHeaderFor(requestUrl);
  if (preferencesFor && !headers.has(PREFERENCES_FOR_HEADER)) {
    headers.set(PREFERENCES_FOR_HEADER, preferencesFor);
  }
  const shouldProfileRuntimeRequest = runtimeRequestProfiling.isRuntimeProfileTarget(requestUrl);
  const requestBodyBytes = shouldProfileRuntimeRequest
    ? estimateRequestBodyBytes(requestInit.body)
    : null;
  const startedAt = shouldProfileRuntimeRequest && typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : null;

  const finishPreferencesWrite = isUserUiPreferencesUrl(requestUrl) && /^(POST|PATCH|PUT|DELETE)$/i.test(requestInit.method || "GET")
    ? beginUserUiPreferencesWrite() : null;
  try {
    const response = await resolvedFetch(input, {
      ...requestInit,
      headers,
    });

    if (response.status === 401 && accessToken) {
      void invalidateRejectedSupabaseSession();
    }

    if (shouldRequestDeploymentVersionCheck(response.status)) {
      requestDeploymentVersionCheck(`api_status_${response.status}`);
    }

    if (shouldProfileRuntimeRequest) {
      const completedAt = typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : null;
      runtimeRequestProfiling.recordRuntimeRequestProfile({
        url: requestUrl,
        method: requestInit.method || "GET",
        durationMs: startedAt != null && completedAt != null ? completedAt - startedAt : null,
        requestBodyBytes,
        response,
      });
    }

    return response;
  } catch (error) {
    if (error?.name !== "AbortError") {
      requestDeploymentVersionCheck("api_network_error");
    }
    if (shouldProfileRuntimeRequest) {
      const completedAt = typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : null;
      runtimeRequestProfiling.recordRuntimeRequestProfile({
        url: requestUrl,
        method: requestInit.method || "GET",
        durationMs: startedAt != null && completedAt != null ? completedAt - startedAt : null,
        requestBodyBytes,
        error,
      });
    }

    throw error;
  } finally {
    finishPreferencesWrite?.();
  }
}

export function subscribeToSupabaseAuthChanges(onSessionChange) {
  let isDisposed = false;
  let notificationSequence = 0;

  const notifyPreferredSession = async (sessionOverride = undefined) => {
    const currentNotificationSequence = ++notificationSequence;
    if (isDisposed) {
      return;
    }

    if (sessionOverride !== undefined && sessionOverride !== null) {
      onSessionChange(sessionOverride);
      return;
    }

    const { error, session } = await getSupabaseSession();
    if (isDisposed || error || currentNotificationSequence !== notificationSequence) {
      return;
    }

    onSessionChange(session ?? null);
  };

  queueMicrotask(() => {
    void notifyPreferredSession();
  });

  let unsubscribeSupabase = () => {};
  if (supabase) {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      void notifyPreferredSession(session ?? null);
    });

    unsubscribeSupabase = () => {
      data.subscription.unsubscribe();
    };
  }

  let removeBypassListeners = () => {};
  if (localAuthBypassEnabled && supabaseWindow?.addEventListener) {
    const notifySessionChange = () => {
      void notifyPreferredSession();
    };

    supabaseWindow.addEventListener(LOCAL_AUTH_BYPASS_SESSION_STATE_CHANGE_EVENT, notifySessionChange);

    removeBypassListeners = () => {
      supabaseWindow.removeEventListener(LOCAL_AUTH_BYPASS_SESSION_STATE_CHANGE_EVENT, notifySessionChange);
    };
  }

  let removeAuthRejectedListener = () => {};
  if (supabaseWindow?.addEventListener) {
    const notifyRejectedSession = () => {
      notificationSequence += 1;
      if (!isDisposed) {
        onSessionChange(null);
      }
    };
    supabaseWindow.addEventListener(SUPABASE_AUTH_REJECTED_EVENT, notifyRejectedSession);
    removeAuthRejectedListener = () => {
      supabaseWindow.removeEventListener(SUPABASE_AUTH_REJECTED_EVENT, notifyRejectedSession);
    };
  }

  return () => {
    isDisposed = true;
    unsubscribeSupabase();
    removeBypassListeners();
    removeAuthRejectedListener();
  };
}

function estimateRequestBodyBytes(body) {
  if (body == null) {
    return null;
  }

  if (typeof body === "string") {
    try {
      return new TextEncoder().encode(body).length;
    } catch {
      return body.length;
    }
  }

  if (body instanceof URLSearchParams) {
    return estimateRequestBodyBytes(body.toString());
  }

  if (typeof Blob !== "undefined" && body instanceof Blob) {
    return Number.isFinite(body.size) ? body.size : null;
  }

  if (body instanceof ArrayBuffer) {
    return body.byteLength;
  }

  if (ArrayBuffer.isView(body)) {
    return body.byteLength;
  }

  return null;
}
